import { useEffect, useMemo, useState } from 'react'
import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import { getReleases, getRelease, getReleasesStats, getGenres, getStyles, getCountries, getRecommendations, getReleaseNeighbors, getReleaseFacets, getAudioTags, getVideoAudio } from '../api/releases'
import { ReleaseFilters, ReleasesResponse } from '../types/release'

export const releasesKeys = {
  all: ['releases'] as const,
  list: (filters: ReleaseFilters) => [...releasesKeys.all, 'list', filters] as const,
  detail: (id: number) => [...releasesKeys.all, 'detail', id] as const,
  neighbors: (id: number, limit: number, includeSameArtist: boolean) => [...releasesKeys.all, 'neighbors', id, limit, includeSameArtist] as const,
  stats: () => [...releasesKeys.all, 'stats'] as const,
  genres: () => [...releasesKeys.all, 'genres'] as const,
  styles: () => [...releasesKeys.all, 'styles'] as const,
  countries: () => [...releasesKeys.all, 'countries'] as const,
  facets: (filters: Omit<ReleaseFilters, 'genres' | 'styles' | 'page' | 'per_page' | 'sort' | 'order' | 'shuffle'>) =>
    [...releasesKeys.all, 'facets', filters] as const,
  audioTags: (id: number) => [...releasesKeys.all, 'audioTags', id] as const,
  videoAudio: (id: number) => [...releasesKeys.all, 'videoAudio', id] as const,
  recommendations: (filters: Omit<ReleaseFilters, 'release_ids' | 'q'>) =>
    [...releasesKeys.all, 'recommendations', filters] as const,
}

export function useReleases(
  filters: ReleaseFilters = {},
  options?: Omit<UseQueryOptions<ReleasesResponse>, 'queryKey' | 'queryFn'>
) {
  // Search inputs on several pages update `q` on every keystroke. Keep those
  // controlled inputs responsive, but do not turn each intermediate value into
  // a synchronous SQLite count + select request.
  const [debouncedQuery, setDebouncedQuery] = useState(filters.q)
  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedQuery(filters.q), 450)
    return () => window.clearTimeout(timeout)
  }, [filters.q])
  const queryFilters = useMemo(
    () => ({ ...filters, q: debouncedQuery }),
    [filters, debouncedQuery]
  )

  return useQuery({
    queryKey: releasesKeys.list(queryFilters),
    queryFn: () => getReleases(queryFilters),
    ...options,
  })
}

export function useRelease(id: number) {
  return useQuery({
    queryKey: releasesKeys.detail(id),
    queryFn: () => getRelease(id),
    enabled: !!id,
    staleTime: 1000 * 60 * 5,
  })
}

export function useAudioTags(id: number) {
  return useQuery({
    queryKey: releasesKeys.audioTags(id),
    queryFn: () => getAudioTags(id),
    enabled: !!id,
  })
}

export function useVideoAudio(id: number) {
  return useQuery({
    queryKey: releasesKeys.videoAudio(id),
    queryFn: () => getVideoAudio(id),
    enabled: !!id,
    staleTime: 1000 * 60 * 5,
  })
}

export function useReleasesStats() {
  return useQuery({
    queryKey: releasesKeys.stats(),
    queryFn: () => getReleasesStats(),
  })
}

export function useGenres(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: releasesKeys.genres(),
    queryFn: () => getGenres(),
    staleTime: 1000 * 60 * 60 * 24 * 30, // 30 days
    gcTime: 1000 * 60 * 60 * 24 * 30,
    enabled: options?.enabled !== false,
  })
}

export function useStyles(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: releasesKeys.styles(),
    queryFn: () => getStyles(),
    staleTime: 1000 * 60 * 60 * 24 * 30, // 30 days
    gcTime: 1000 * 60 * 60 * 24 * 30,
    enabled: options?.enabled !== false,
  })
}

export function useCountries(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: releasesKeys.countries(),
    queryFn: () => getCountries(),
    staleTime: 1000 * 60 * 60 * 24 * 30, // 30 days
    gcTime: 1000 * 60 * 60 * 24 * 30,
    enabled: options?.enabled !== false,
  })
}

export function useReleaseFacets(
  filters: Omit<ReleaseFilters, 'genres' | 'styles' | 'page' | 'per_page' | 'sort' | 'order' | 'shuffle'>,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: releasesKeys.facets(filters),
    queryFn: () => getReleaseFacets(filters),
    staleTime: 1000 * 60 * 5,  // 5 min — don't refetch if fresh
    gcTime: 1000 * 60 * 30,   // 30 min — keep in memory long after panel closes
    enabled: options?.enabled !== false,
  })
}

export function useReleaseNeighbors(id: number, limit = 12, includeSameArtist = false) {
  return useQuery({
    queryKey: releasesKeys.neighbors(id, limit, includeSameArtist),
    queryFn: async () => {
      const data = await getReleaseNeighbors(id, limit, includeSameArtist)
      // Deduplicate by release ID (neighbors table can contain duplicates)
      const seen = new Set<number>()
      data.releases = data.releases.filter(r => {
        if (seen.has(r.id)) return false
        seen.add(r.id)
        return true
      })
      return data
    },
    enabled: !!id,
    staleTime: 1000 * 60 * 60,
  })
}

/**
 * Cheap order-sensitive fingerprint of the source ids, so the query key changes
 * when the seed set does. Hashing beats putting the array in the key directly —
 * a source set can hold thousands of ids and React Query re-serialises the key
 * on every render.
 */
function fingerprintSourceIds(ids: number[]): string {
  let hash = 0
  for (let i = 0; i < ids.length; i++) {
    hash = (hash * 31 + ids[i]) | 0
  }
  return `${ids.length}:${hash}`
}

export function useRecommendations(
  sourceIds: number[],
  filters: Omit<ReleaseFilters, 'release_ids' | 'q'> = {},
  options?: Omit<UseQueryOptions<ReleasesResponse>, 'queryKey' | 'queryFn'>,
  useCollectionTaste = false
) {
  return useQuery({
    // The seed ids are the main input to this query but used to be absent from
    // its key, so two callers with matching filters shared a cache entry no
    // matter which records they were asking about.
    queryKey: releasesKeys.recommendations({
      ...filters,
      _taste: useCollectionTaste,
      _sources: fingerprintSourceIds(sourceIds),
    } as Omit<ReleaseFilters, 'release_ids' | 'q'>),
    queryFn: () => getRecommendations(sourceIds, filters, useCollectionTaste),
    ...options,
  })
}
